/**
 * @fileoverview In-memory data behind the user-invitation mocks.
 * @description Assignable roles, eligible reporting managers, invitations and the user directory
 * (GET /users) they add to, held in module state for the browser session (a reload restores the
 * seed). The directory seed and the reporting managers are the same people, with the same ids, as
 * the sales roster mock; the root (the Super Admin) is a reporting manager but, as in the backend,
 * never a directory member. Plus the backend's rules for invitations: an
 * invitation is valid for 48 hours, a PENDING one past its expiry reads as EXPIRED, Resend needs 30
 * seconds since the last send and allows 5 resends, and Resend/Revoke only apply to PENDING or
 * EXPIRED invitations. Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import directorySeed from './directory-users-seed.json'

import type {
  AssignableRole,
  InvitationStatus,
  InvitationSummary,
  ReportingManagerOption,
  SendInvitationRequest,
} from '@/features/user-invitations/types/user-invitations.types'

const HOUR_MS = 60 * 60 * 1000
const INVITATION_VALIDITY_MS = 48 * HOUR_MS
export const RESEND_COOLDOWN_MS = 30 * 1000
export const MAX_RESENDS = 5

interface MockRole {
  roleId: string
  roleCode: string
  name: string
}

export type MockPositionCode = 'PLATFORM_ADMIN' | 'SALES_HEAD' | 'SALES_MANAGER' | 'SALES_EXECUTIVE'

/** A directory entry's reporting manager. */
export interface MockManagerRef {
  userId: string
  displayName: string
  employeeCode: string | null
  positionCode: MockPositionCode
}

/** A state from the catalogue, as a directory entry carries it. */
export interface MockState {
  stateId: string
  code: string
  name: string
  kind: 'STATE' | 'UNION_TERRITORY'
  status: 'ACTIVE' | 'INACTIVE'
}

/**
 * The states the mock offers a user. Their ids match the sales mock's prospect-metadata states
 * (GET /tenants/prospect-metadata), which Add and Edit User take their State choices from.
 */
export const MOCK_STATES: MockState[] = [
  ['IN-MH', 'Maharashtra'],
  ['IN-GJ', 'Gujarat'],
  ['IN-KA', 'Karnataka'],
  ['IN-TG', 'Telangana'],
  ['IN-TN', 'Tamil Nadu'],
  ['IN-UP', 'Uttar Pradesh'],
].map(([code, name], index) => ({
  stateId: `00000000-0000-4000-8002-${String(index + 1).padStart(12, '0')}`,
  code: code ?? '',
  name: name ?? '',
  kind: 'STATE',
  status: 'ACTIVE',
}))

/**
 * @description Finds a mock state by id.
 * @param {string | null | undefined} stateId - The state's id.
 * @returns {MockState | null} The state, or null (All India) when the id names none.
 */
export function findMockState(stateId: string | null | undefined): MockState | null {
  return MOCK_STATES.find((state) => state.stateId === stateId) ?? null
}

/** A GET /users entry (the users feature's DirectoryUser; features can't import each other). */
export interface MockDirectoryUser {
  userId: string
  employeeCode: string | null
  firstName: string
  middleName: string | null
  lastName: string
  displayName: string
  email: string
  avatar: null
  countryCode: string | null
  mobileNumber: string | null
  primaryPosition: { code: MockPositionCode; name: string }
  department: {
    departmentId: string
    departmentCode: string
    name: string
    status: 'ACTIVE' | 'INACTIVE'
  } | null
  reportingManager: MockManagerRef | null
  joinedAt: string | null
  dateOfJoining: string | null
  status: 'INVITED' | 'REGISTERED' | 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'SUSPENDED' | 'DEACTIVATED'
  directoryStatus: 'PENDING' | 'ACTIVE' | 'INACTIVE'
  roles: MockRole[]
  /** Null means All India. */
  state: MockState | null
}

interface MockInvitationRecord extends InvitationSummary {
  lastSentAt: number
  resendCount: number
  /** The invitee's profile, role and reporting manager, for their GET /users entry. */
  employeeCode: string
  firstName: string
  middleName: string | null
  lastName: string
  countryCode: string | null
  mobileNumber: string | null
  role: MockRole
  reportingManager: MockManagerRef | null
  state: MockState | null
  /** Set once the invitee has been deactivated, so they stay in the directory, now INACTIVE. */
  deactivated: boolean
}

export const MOCK_SALES_DEPARTMENT = {
  departmentId: 'mock-department-sales',
  departmentCode: 'SALES',
  name: 'Sales',
  status: 'ACTIVE',
} as const

// directory-users-seed.json's string unions widen to `string` on import, so this restores them.
const directoryUsers = directorySeed as MockDirectoryUser[]

export const MOCK_ASSIGNABLE_ROLES: AssignableRole[] = [
  {
    roleId: 'mock-role-bank-branch-manager',
    roleCode: 'BANK_BRANCH_MANAGER',
    name: 'Bank Branch Manager',
    roleCategory: 'EXTERNAL',
    allowedScopeTypes: ['BRANCH'],
    requiresTargetContext: true,
    requiresStepUpMfa: false,
  },
  {
    roleId: 'mock-role-sales-head',
    roleCode: 'SALES_HEAD',
    name: 'Sales Head',
    roleCategory: 'INTERNAL',
    allowedScopeTypes: ['PLATFORM'],
    requiresTargetContext: false,
    requiresStepUpMfa: false,
  },
  {
    roleId: 'mock-role-sales-manager',
    roleCode: 'SALES_MANAGER',
    name: 'Sales Manager',
    roleCategory: 'INTERNAL',
    allowedScopeTypes: ['PLATFORM'],
    requiresTargetContext: false,
    requiresStepUpMfa: false,
  },
  {
    roleId: 'mock-role-sales-executive',
    roleCode: 'SALES_EXECUTIVE',
    name: 'Sales Executive',
    roleCategory: 'INTERNAL',
    allowedScopeTypes: ['PLATFORM'],
    requiresTargetContext: false,
    requiresStepUpMfa: false,
  },
]

const SUPER_ADMIN: ReportingManagerOption = {
  userId: 'EMP-2000',
  displayName: 'Hrishikesh Patwardhan',
  positionCode: 'PLATFORM_ADMIN',
}
const SALES_HEADS: ReportingManagerOption[] = [
  { userId: 'EMP-2001', displayName: 'Hardik Mehta', positionCode: 'SALES_HEAD' },
]
const SALES_MANAGERS: ReportingManagerOption[] = [
  { userId: 'EMP-2002', displayName: 'Saurabh Rane', positionCode: 'SALES_MANAGER' },
  { userId: 'EMP-2003', displayName: 'Vikram Desai', positionCode: 'SALES_MANAGER' },
]
const SALES_EXECUTIVE_ROLE: MockRole = {
  roleId: 'mock-role-sales-executive',
  roleCode: 'SALES_EXECUTIVE',
  name: 'Sales Executive',
}

/** Everyone a Sales user may report to. */
export const MOCK_MANAGERS: ReportingManagerOption[] = [
  SUPER_ADMIN,
  ...SALES_HEADS,
  ...SALES_MANAGERS,
]

/** Who each Sales role may report to, by role id. */
const MANAGERS_BY_ROLE: Record<string, ReportingManagerOption[]> = {
  'mock-role-sales-head': [SUPER_ADMIN],
  'mock-role-sales-manager': SALES_HEADS,
  'mock-role-sales-executive': [...SALES_HEADS, ...SALES_MANAGERS],
}

/**
 * @description Builds a directory reporting-manager reference from a manager's id.
 * @param {string | undefined} userId - The manager's id.
 * @returns {MockManagerRef | null} The reference, or null when the id names no manager.
 */
export function toManagerRef(userId: string | undefined): MockManagerRef | null {
  const manager = MOCK_MANAGERS.find((candidate) => candidate.userId === userId)
  return manager
    ? {
        userId: manager.userId,
        displayName: manager.displayName,
        // The mock's user ids are its employee codes.
        employeeCode: manager.userId,
        positionCode: manager.positionCode as MockPositionCode,
      }
    : null
}

interface SeedInput {
  id: string
  /** Defaults to a fresh mock invitee id. */
  userId?: string
  displayName: string
  email: string
  hoursAgo: number
  status: InvitationStatus
}

/**
 * @description Builds an invitation record sent the given number of hours ago.
 * @param {SeedInput} input - Its id suffix, invitee, age and stored status.
 * @returns {MockInvitationRecord} The record.
 */
function seed({
  id,
  userId,
  displayName,
  email,
  hoursAgo,
  status,
}: SeedInput): MockInvitationRecord {
  const sentAt = Date.now() - hoursAgo * HOUR_MS
  return {
    invitationId: `mock-invitation-${id}`,
    userId: userId ?? `mock-invitee-${id}`,
    email,
    displayName,
    status,
    createdAt: new Date(sentAt).toISOString(),
    expiresAt: new Date(sentAt + INVITATION_VALIDITY_MS).toISOString(),
    acceptedAt: status === 'ACCEPTED' ? new Date(sentAt + HOUR_MS).toISOString() : null,
    revokedAt: status === 'REVOKED' ? new Date(sentAt + HOUR_MS).toISOString() : null,
    lastSentAt: sentAt,
    resendCount: 0,
    employeeCode: userId ?? `EMP-21${id.slice(-2).padStart(2, '0')}`,
    firstName: displayName.split(' ')[0] ?? displayName,
    middleName: null,
    lastName: displayName.split(' ').slice(1).join(' '),
    countryCode: null,
    mobileNumber: null,
    role: SALES_EXECUTIVE_ROLE,
    reportingManager: toManagerRef('EMP-2002'),
    state: null,
    deactivated: false,
  }
}

const invitations: MockInvitationRecord[] = [
  seed({
    id: '1',
    displayName: 'Ananya Joshi',
    email: 'ananya@insightbox.tech',
    hoursAgo: 2,
    status: 'PENDING',
  }),
  seed({
    id: '2',
    displayName: 'Rohan Deshpande',
    email: 'rohan@insightbox.tech',
    hoursAgo: 72,
    status: 'PENDING',
  }),
  seed({
    id: '3',
    displayName: 'Meera Iyer',
    email: 'meera@insightbox.tech',
    hoursAgo: 120,
    status: 'ACCEPTED',
  }),
  seed({
    id: '4',
    displayName: 'Kabir Shah',
    email: 'kabir@insightbox.tech',
    hoursAgo: 30,
    status: 'REVOKED',
  }),
  // The sales roster's Pending Invitation user, so its row's Resend/Revoke actions work.
  {
    ...seed({
      id: '5',
      userId: 'EMP-2008',
      displayName: 'Kavya Reddy',
      email: 'kavya@insightbox.in',
      hoursAgo: 20,
      status: 'PENDING',
    }),
    reportingManager: toManagerRef('EMP-2003'),
  },
]

/**
 * @description An invitation's effective status: PENDING past its expiry reads as EXPIRED.
 * @param {MockInvitationRecord} record - The stored invitation.
 * @returns {InvitationStatus} The status the API reports.
 */
function effectiveStatus(record: MockInvitationRecord): InvitationStatus {
  return record.status === 'PENDING' && Date.parse(record.expiresAt) <= Date.now()
    ? 'EXPIRED'
    : record.status
}

/**
 * @description Strips the mock-only bookkeeping and applies the effective status.
 * @param {MockInvitationRecord} record - The stored invitation.
 * @returns {InvitationSummary} The API shape.
 */
function toSummary(record: MockInvitationRecord): InvitationSummary {
  return {
    invitationId: record.invitationId,
    userId: record.userId,
    email: record.email,
    displayName: record.displayName,
    status: effectiveStatus(record),
    createdAt: record.createdAt,
    expiresAt: record.expiresAt,
    acceptedAt: record.acceptedAt,
    revokedAt: record.revokedAt,
  }
}

/**
 * @description Lists the eligible reporting managers for a role.
 * @param {string} roleId - The role being invited.
 * @returns {ReportingManagerOption[]} The managers (none for non-Sales roles).
 */
export function listEligibleManagers(roleId: string): ReportingManagerOption[] {
  return MANAGERS_BY_ROLE[roleId] ?? []
}

/**
 * @description Lists invitations newest first, filtered by effective status and name/email search.
 * @param {InvitationStatus | undefined} status - The status filter.
 * @param {string | undefined} search - The search text.
 * @returns {InvitationSummary[]} The matching invitations.
 */
export function listInvitations(
  status: InvitationStatus | undefined,
  search: string | undefined,
): InvitationSummary[] {
  const needle = search?.trim().toLowerCase() ?? ''
  return invitations
    .map(toSummary)
    .filter((invitation) => !status || invitation.status === status)
    .filter(
      (invitation) =>
        !needle || `${invitation.displayName} ${invitation.email}`.toLowerCase().includes(needle),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/**
 * @description Whether an email already has an account-blocking PENDING invitation.
 * @param {string} email - The email to check.
 * @returns {boolean} True when it can't be invited again yet.
 */
export function hasPendingInvitation(email: string): boolean {
  const normalized = email.trim().toLowerCase()
  return invitations.some(
    (record) => record.email === normalized && effectiveStatus(record) === 'PENDING',
  )
}

/**
 * @description Creates a PENDING invitation.
 * @param {SendInvitationRequest} request - The send request.
 * @returns {InvitationSummary} The new invitation.
 */
export function createInvitation(request: SendInvitationRequest): InvitationSummary {
  const id = `${Date.now()}`
  const record = seed({
    id,
    displayName: [request.firstName, request.middleName, request.lastName]
      .filter(Boolean)
      .join(' '),
    email: request.email.trim().toLowerCase(),
    hoursAgo: 0,
    status: 'PENDING',
  })
  const assignedRole = MOCK_ASSIGNABLE_ROLES.find(
    (candidate) => candidate.roleId === request.assignments[0]?.roleId,
  )
  if (assignedRole) {
    record.role = {
      roleId: assignedRole.roleId,
      roleCode: assignedRole.roleCode,
      name: assignedRole.name,
    }
  }
  record.employeeCode = `EMP-${2100 + invitations.length}`
  record.firstName = request.firstName
  record.middleName = request.middleName ?? null
  record.lastName = request.lastName
  record.countryCode = request.countryCode ?? null
  record.mobileNumber = request.mobileNumber ?? null
  // A Sales Head invited without one reports to the inviting Super Admin.
  record.reportingManager = toManagerRef(
    request.reportingManagerUserId ??
      (record.role.roleCode === 'SALES_HEAD' ? SUPER_ADMIN.userId : undefined),
  )
  record.state = findMockState(request.stateId)
  invitations.push(record)
  return toSummary(record)
}

/**
 * @description Lists the user directory: the seeded users plus everyone invited since, who reads as
 * INVITED until they accept. A revoked or accepted invitation's invitee isn't listed (the seed
 * already holds every active user).
 * @returns {MockDirectoryUser[]} The directory, unfiltered.
 */
export function listDirectoryUsers(): MockDirectoryUser[] {
  const seededIds = new Set(directoryUsers.map((user) => user.userId))
  const invited = invitations
    .filter(
      (record) => !seededIds.has(record.userId) && (isManageable(record) || record.deactivated),
    )
    .map((record): MockDirectoryUser => ({
      userId: record.userId,
      employeeCode: record.employeeCode,
      firstName: record.firstName,
      middleName: record.middleName,
      lastName: record.lastName,
      displayName: `${record.firstName} ${record.lastName}`,
      email: record.email,
      avatar: null,
      countryCode: record.countryCode,
      mobileNumber: record.mobileNumber,
      primaryPosition: {
        code: record.role.roleCode as MockPositionCode,
        name: record.role.name,
      },
      department: MOCK_SALES_DEPARTMENT,
      reportingManager: record.reportingManager,
      joinedAt: null,
      dateOfJoining: null,
      status: 'INVITED',
      directoryStatus: 'PENDING',
      roles: [record.role],
      state: record.state,
    }))
  return [...directoryUsers, ...invited]
}

/**
 * @description Finds a stored invitation.
 * @param {string} invitationId - The invitation id.
 * @returns {MockInvitationRecord | undefined} The record.
 */
export function findInvitation(invitationId: string): MockInvitationRecord | undefined {
  return invitations.find((record) => record.invitationId === invitationId)
}

/**
 * @description Whether Resend/Revoke may act on the invitation.
 * @param {MockInvitationRecord} record - The stored invitation.
 * @returns {boolean} True for PENDING or EXPIRED.
 */
export function isManageable(record: MockInvitationRecord): boolean {
  const status = effectiveStatus(record)
  return status === 'PENDING' || status === 'EXPIRED'
}

/**
 * @description Re-sends: a fresh 48-hour link, back to PENDING.
 * @param {MockInvitationRecord} record - The stored invitation.
 * @returns {InvitationSummary} The updated invitation.
 */
export function resend(record: MockInvitationRecord): InvitationSummary {
  const now = Date.now()
  record.status = 'PENDING'
  record.lastSentAt = now
  record.resendCount += 1
  record.expiresAt = new Date(now + INVITATION_VALIDITY_MS).toISOString()
  return toSummary(record)
}

/**
 * @description Revokes the invitation (idempotent).
 * @param {MockInvitationRecord} record - The stored invitation.
 */
export function revoke(record: MockInvitationRecord): void {
  if (record.status !== 'REVOKED') {
    record.status = 'REVOKED'
    record.revokedAt = new Date().toISOString()
  }
}

/**
 * @description The invitation Resend User Invitation takes for a user still waiting to accept.
 * @param {string} userId - The user's id.
 * @returns {string | null} The invitation id, or null when they have no manageable invitation.
 */
export function pendingInvitationIdOf(userId: string): string | null {
  return (
    invitations.find((record) => record.userId === userId && isManageable(record))?.invitationId ??
    null
  )
}

/**
 * @description Deactivating a user who never accepted: their invitation is revoked, and they stay
 * in the directory as a deactivated user.
 * @param {string} userId - The invited user's id.
 */
export function revokeInvitationOnDeactivation(userId: string): void {
  for (const record of invitations) {
    if (record.userId === userId && isManageable(record)) {
      revoke(record)
      record.deactivated = true
    }
  }
}

/**
 * @description Reactivating a user who never accepted: they are invited again, with a fresh link.
 * @param {string} userId - The user's id.
 */
export function restartInvitation(userId: string): void {
  for (const record of invitations) {
    if (record.userId === userId && record.status === 'REVOKED') {
      record.status = 'PENDING'
      record.revokedAt = null
      record.deactivated = false
      record.lastSentAt = Date.now()
      record.expiresAt = new Date(Date.now() + INVITATION_VALIDITY_MS).toISOString()
    }
  }
}
